import React, { useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { add } from 'date-fns';
import { OnChange } from 'react-final-form-listeners';
import { FormSpy } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { setSelectedBaby } from '../../actions/ui';

import {
  FormLabel,
  FormInputHolder,
  FormInput,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormDateInput,
  FormTimeInput
} from '../form';
import babyIcons from '../icons/babyIcons';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import Temperature from './FieldExcerpts/Temperature';
import Duration, { addDateAndTimeFields, getDurationStrict } from './Duration';

import { required, checkTime } from '../../helpers/formValidators';
import { Error } from '../form/BaseForm';

import './ActivityForm.scss';
import { FormContext } from '../../context/form-context';
import { PrimaryButton, SecondaryButton } from '../buttons';

const ActivityForm = props => {
  const {
    babies,
    chosenActivity,
    selectedProfile,
    basicRecordsPost,
    basicRecordsPatch,
    accountId,
    hideTrackerModals,
    handleRecords,
    profileOptions,
    showUnSavedModal,
    showSaveEntryModal,
    showFirstTimeSavingModal,
    basicModalShown = false,
    selectedBabyId = null
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeDashboardBabyId = useSelector(state => state.ui.selectedBabyId);

  const [initialValues, setInitialValues] = useState(null);
  const formContext = useContext(FormContext);

  const defaultValues = useMemo(() => ({
    babyId: selectedBabyId,
    providerId: selectedProfile?.id,
    date: new Date(),
    startTime: new Date(),
    endTime: add(new Date(), { minutes: 10 })
  }), [selectedProfile?.id, selectedBabyId]);

  useEffect(() => {
    if (!basicModalShown) {
      setInitialValues(null);
      formContext.clearFormContext();
      return;
    }

    if (formContext.formValues && Object.keys(formContext.formValues).length > 0) {
      setInitialValues({ ...formContext.formValues });
      return;
    }

    const latestRecord = handleRecords('basic', selectedBabyId) || {};
    setInitialValues({
      ...defaultValues,
      ...latestRecord,
      babyId: selectedBabyId
    });
  }, [basicModalShown, selectedBabyId]);

  const toggleWarningModal = () =>
    formContext.isDirty ? showUnSavedModal() : hideTrackerModals();

  const onSubmit = values => {
    const hasDuration = !!chosenActivity.duration;

    const recordBody = {
      ...values,
      activityType: chosenActivity.value,
      providerId: values.providerId === 'other' ? null : values.providerId,
      otherProvider:
        values.providerId !== 'other' ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.startTime),
      duration: hasDuration
        ? getDurationStrict(values.startTime, values.endTime)
        : 0,
      endTime: hasDuration
        ? addDateAndTimeFields(values.date, values.endTime)
        : null
    };

    const originalBabyId = initialValues?.babyId;
    const newBabyId = values.babyId || originalBabyId;

    const handleSuccess = (data, shouldNavigate = false) => {
      if (data) {
        dispatch(api.actions.basicRecordsList({ accountId, babyId: originalBabyId }));
        
        if (newBabyId !== originalBabyId) {
          dispatch(api.actions.basicRecordsList({ accountId, babyId: newBabyId }));
        }
        
        dispatch(api.actions.basicRecordsLatest({ accountId }));
        hideTrackerModals();

        if (shouldNavigate) {
          dispatch(setSelectedBaby(newBabyId));
          navigate('/dashboard/activities');
        }
      }
    };

    if (values.id) {
      return basicRecordsPatch(
        { accountId, babyId: originalBabyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => handleSuccess(data, false))
        .catch(() => <ErrorMessage />);
    }
    
    return basicRecordsPost(
      { accountId, babyId: newBabyId },
      { body: JSON.stringify(recordBody) }
    )
      .then((data) => handleSuccess(data, true))
      .catch(() => <ErrorMessage />);
  };

  const onSave = values => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({ onSubmit });
      return showSaveEntryModal();
    }
  };

  if (!basicModalShown || !initialValues) return null;

  return (
    <BaseForm
      key={initialValues.id || 'new'}
      onSubmit={onSave}
      validate={chosenActivity && chosenActivity.duration ? checkTime : null}
      initialValues={initialValues}
    >
      <h2>Track {chosenActivity && chosenActivity.label}</h2>

      {babies && babies.length > 1 && !initialValues?.id && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby) => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) return;
              const latestRecord = handleRecords('basic', value) || {};
              setInitialValues({
                ...initialValues,
                ...latestRecord,
                babyId: value
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      {babies && initialValues?.id && (() => {
        const editBaby = babies.find(b => b.id === initialValues.babyId);
        return editBaby ? (
          <FormIconChooser>
            <label className="form-radio form-radio--checked">
              <div className="form-radio__image-container">
                <img src={babyIcons[editBaby.icon]} alt={editBaby.name} className="form-radio__image" width="2.4rem" height="2.4rem" />
              </div>
              <span className="form-radio__label">{editBaby.name}</span>
            </label>
          </FormIconChooser>
        ) : null;
      })()}

      <FormLabel htmlFor="providerId">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={profileOptions}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      {chosenActivity && chosenActivity.value === 'other' && (
        <FormInput
          name="otherActivity"
          type="text"
          labelRenderer={() => (
            <FormLabel htmlFor="otherActivity">Describe Other Activity</FormLabel>
          )}
        />
      )}

      {chosenActivity && chosenActivity.duration ? (
        <Duration />
      ) : (
        <FormInputHolder>
          <FormDateInput
            name="date"
            labelRenderer={() => <FormLabel htmlFor="date">Date</FormLabel>}
            dateFormat="MMM, dd yyyy"
            validate={required}
          />
          <FormTimeInput
            name="startTime"
            labelRenderer={() => <FormLabel htmlFor="startTime">Time</FormLabel>}
            validate={required}
          />
        </FormInputHolder>
      )}

      {chosenActivity && chosenActivity.value === 'temperature' && (
        <FormInputHolder>
          <Temperature />
        </FormInputHolder>
      )}

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => (
          <FormInputHolder>
            <SecondaryButton
              type="button"
              label="Cancel"
              onClick={toggleWarningModal}
            />
            <PrimaryButton type="submit" label="Save" disabled={submitting} />
          </FormInputHolder>
        )}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          setTimeout(() => {
            if (dirty) {
              formContext.setValues(values);
              const actuallyDirty = Object.values(visited).some(Boolean);
              actuallyDirty ? formContext.setDirty() : formContext.notDirty();
            }
          }, 0);
        }}
      />
    </BaseForm>
  );
};

ActivityForm.propTypes = {
  babies: PropTypes.arrayOf(PropTypes.object),
  chosenActivity: PropTypes.object,
  selectedProfile: PropTypes.object,
  basicRecordsPost: PropTypes.func,
  basicRecordsPatch: PropTypes.func,
  accountId: PropTypes.string,
  hideTrackerModals: PropTypes.func,
  handleRecords: PropTypes.func,
  profileOptions: PropTypes.arrayOf(PropTypes.object),
  showUnSavedModal: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  showFirstTimeSavingModal: PropTypes.bool,
  basicModalShown: PropTypes.bool,
  selectedBabyId: PropTypes.string
};

export default ActivityForm;
